// Client model for the async report pipeline (apps/api reports handler +
// apps/consumer worker): POST /api/reports/{type} writes a report_request doc
// into the tenant DB; the consumer renders PDF/CSV, uploads to S3 and writes a
// `report` doc (source = request id, s3_url = presigned download) back. Both
// docs replicate into local PouchDB where this module reads them.

import { getPouchDB } from '@/lib/couch';

export enum ReportFormat {
  PDF = 'pdf',
  CSV = 'csv',
}

export enum ReportStatus {
  QUEUED = 'queued',
  DONE = 'done',
  FAILED = 'failed',
}

export interface ReportDoc {
  _id: string;
  type: string; // 'report' | 'report_request'
  report_type: string;
  status: string;
  format?: string;
  requested_at?: string;
  requested_by?: string;
  completed_at?: string;
  s3_url?: string;
  source?: string;
  error?: string;
}

export interface ReportRow {
  id: string;
  reportType: string;
  format: string;
  status: ReportStatus;
  requestedAt: string;
  downloadUrl?: string;
  error?: string;
}

/**
 * Join report_request docs with their result `report` docs (matched via
 * report.source) into display rows, newest first.
 */
export function latestReports(docs: ReportDoc[], limit = 20): ReportRow[] {
  const results = new Map<string, ReportDoc>();
  for (const d of docs) {
    if (d.type === 'report' && d.source) results.set(d.source, d);
  }
  return docs
    .filter(d => d.type === 'report_request')
    .map(req => {
      const res = results.get(req._id);
      const status =
        res?.status === ReportStatus.DONE
          ? ReportStatus.DONE
          : (req.status as ReportStatus) || ReportStatus.QUEUED;
      return {
        id: req._id,
        reportType: req.report_type,
        format: res?.format ?? req.format ?? ReportFormat.PDF,
        status,
        requestedAt: req.requested_at ?? '',
        downloadUrl: res?.s3_url,
        error: req.error,
      };
    })
    .sort((a, b) => b.requestedAt.localeCompare(a.requestedAt))
    .slice(0, limit);
}

/**
 * Read all report + report_request docs from local PouchDB. Their _ids use a
 * `report_` / `report_request_` underscore scheme (not the `<type>:` colon
 * scheme the store-adapter understands), so read the shared prefix directly.
 */
export async function readReportDocs(): Promise<ReportDoc[]> {
  const db = getPouchDB();
  const res = await db.allDocs<ReportDoc>({
    include_docs: true,
    startkey: 'report_',
    endkey: 'report_￰',
  });
  return res.rows
    .map(r => r.doc as ReportDoc | undefined)
    .filter(
      (d): d is ReportDoc =>
        Boolean(d) && (d!.type === 'report' || d!.type === 'report_request')
    );
}
